import { AddOn } from '../types/Package';
import { API_BASE_URL } from './apiBase';

/**
 * Default global addons
 */
const DEFAULT_ADDONS: AddOn[] = [
  {
    id: 'studio-time',
    name: 'Studio Time',
    price: 2500,
    pricingType: 'per-hour',
    currency: 'R',
    description: 'Studio rental time charged per hour. Perfect for controlled lighting setups and professional backdrops.',
  },
  {
    id: 'hair-makeup',
    name: 'Hair & Makeup',
    price: 2500,
    pricingType: 'fixed',
    currency: 'R',
    description: 'Professional hair and makeup services for your session.',
  },
  {
    id: 'styling',
    name: 'Styling',
    price: 1500,
    pricingType: 'fixed',
    currency: 'R',
    description: 'Professional styling consultation and wardrobe coordination.',
  },
  {
    id: 'lighting-setup',
    name: 'Lighting Setup',
    price: 2000,
    pricingType: 'fixed',
    currency: 'R',
    description: 'Professional lighting equipment and setup for enhanced photography.',
  },
  {
    id: 'backdrops',
    name: 'Backdrops',
    price: 500,
    pricingType: 'per-each',
    currency: 'R',
    description: 'Additional backdrop options for variety in your session.',
  },
];

/**
 * Load addons from localStorage
 */
export const loadAddons = (): AddOn[] => {
  try {
    const savedAddons = localStorage.getItem('admin_addons');
    if (savedAddons) {
      const parsed = JSON.parse(savedAddons);
      // If we have saved addons, return them
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
    // If no addons exist, initialize with defaults
    saveAddons(DEFAULT_ADDONS);
    return DEFAULT_ADDONS;
  } catch (error) {
    console.error('Error loading addons:', error);
    // On error, return defaults
    return DEFAULT_ADDONS;
  }
};

/**
 * Load addons from server (LIVE mode)
 */
export const loadAddonsFromServer = async (): Promise<AddOn[]> => {
  const allowLocalFallback = process.env.REACT_APP_ALLOW_LOCAL_FALLBACK === 'true';
  try {
    // Add cache-busting query parameter and no-cache option to prevent stale data on Render
    const cacheBuster = Date.now();
    const url = `${API_BASE_URL}/addons?v=${cacheBuster}`;
    console.log('📦 [CLIENT] Fetching addons from:', url);
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    console.log('📦 [CLIENT] Response status:', response.status, response.statusText);
    console.log('📦 [CLIENT] Response headers:', Object.fromEntries(response.headers.entries()));
    console.log('📦 [CLIENT] Response URL:', response.url);
    
    if (response.ok) {
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('📦 [CLIENT] Expected JSON but got:', contentType);
        console.error('📦 [CLIENT] Response text (first 500 chars):', text.substring(0, 500));
        if (allowLocalFallback) {
          console.log('📦 [CLIENT] Local fallback enabled, loading addons from localStorage');
          return loadAddons();
        }
        return [];
      }
      
      const data = await response.json();
      console.log('📦 [CLIENT] Raw response data:', JSON.stringify(data, null, 2));
      const serverAddons = data.addons || [];
      console.log('📦 [CLIENT] Parsed addons:', serverAddons.length);
      console.log('📦 [CLIENT] Addon details:', JSON.stringify(serverAddons, null, 2));
      return serverAddons;
    }
    
    // Try to read the error response
    const errorText = await response.text().catch(() => 'Could not read error response');
    console.warn('📦 [CLIENT] Response not OK, status:', response.status);
    console.warn('📦 [CLIENT] Error response (first 500 chars):', errorText.substring(0, 500));
    if (allowLocalFallback) {
      console.warn('📦 [CLIENT] Local fallback enabled - using localStorage addons');
      return loadAddons();
    }
    return [];
  } catch (error) {
    console.error('📦 [CLIENT] Error loading addons from server:', error);
    if (error instanceof Error) {
      console.error('📦 [CLIENT] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    } else {
      console.error('📦 [CLIENT] Unknown error type:', error);
    }
    if (allowLocalFallback) {
      console.warn('📦 [CLIENT] Local fallback enabled - using localStorage addons');
      return loadAddons();
    }
    return [];
  }
};

/**
 * Save addons to localStorage
 */
export const saveAddons = (addons: AddOn[]): void => {
  try {
    localStorage.setItem('admin_addons', JSON.stringify(addons));
    // Dispatch event for other components to listen
    window.dispatchEvent(new Event('addons_updated'));
  } catch (error) {
    console.error('Error saving addons:', error);
  }
};

/**
 * Save addons to server (LIVE mode)
 */
export const saveAddonsToServer = async (addons: AddOn[]): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/addons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ addons }),
    });
    
    if (response.ok) {
      // Also save to localStorage as backup
      saveAddons(addons);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error saving addons to server:', error);
    // Save to localStorage as fallback
    saveAddons(addons);
    return false;
  }
};

/**
 * Get addon by ID
 */
export const getAddonById = (id: string, addons?: AddOn[]): AddOn | undefined => {
  const addonsList = addons || loadAddons();
  return addonsList.find(addon => addon.id === id);
};

/**
 * Get addon by name
 */
export const getAddonByName = (name: string, addons?: AddOn[]): AddOn | undefined => {
  const addonsList = addons || loadAddons();
  return addonsList.find(addon => addon.name.toLowerCase() === name.toLowerCase());
};

